import { gql } from '@apollo/client';

// --- Fragments ---

export const SHARE_LINK_LIST_FIELDS = gql`
  fragment ShareLinkListFields on ShareLink {
    _id
    objectId
    type
    subType
    accountId
    teamId
    createdAt
    lastUpdatedDate
    access {
      type
      role
      ts
    }
    securities {
      userId
      role
      ts
    }
    password {
      enabled
      ts
    }
    expiration {
      enabled
      value
      ts
    }
  }
`;

// --- Admin Queries ---

/**
 * List share links for admin management page.
 * Backend resolver: shareLinks (paginated, Relay connection)
 */
export const SHARE_LINKS_QUERY = gql`
  query ShareLinks(
    $teamId: String
    $first: Int
    $after: String
    $query: QueryShareLinkInput
    $sort: ShareLinkSort
  ) {
    shareLinks(teamId: $teamId, first: $first, after: $after, query: $query, sort: $sort) {
      page {
        edges {
          cursor
          node {
            _id
            objectId
            type
            subType
            accountId
            teamId
            createdAt
            lastUpdatedDate
            access {
              type
              role
              ts
            }
            securities {
              userId
              role
              ts
            }
            password {
              enabled
              ts
            }
            expiration {
              enabled
              value
              ts
            }
          }
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
      pageData {
        limit
        offset
      }
    }
  }
`;

/**
 * Get a single share link by objectId (for modal).
 * Returns null if no share link exists.
 */
export const GET_SHARE_LINK_BY_OBJECT_ID = gql`
  query ShareLinkByObjectId($objectId: String!) {
    shareLinkByObjectId(objectId: $objectId) {
      _id
      objectId
      type
      subType
      accountId
      teamId
      createdAt
      lastUpdatedDate
      access {
        type
        role
        ts
      }
      securities {
        userId
        role
        ts
      }
      password {
        enabled
        ts
      }
      expiration {
        enabled
        value
        ts
      }
    }
  }
`;

// --- Admin Mutations ---

export const CREATE_SHARE_LINK = gql`
  mutation CreateShareLink($payload: CreateShareLinkInput!) {
    createShareLink(payload: $payload) {
      _id
      objectId
      type
      subType
      access {
        type
        role
        ts
      }
      securities {
        userId
        role
        ts
      }
      password {
        enabled
        ts
      }
      expiration {
        enabled
        value
        ts
      }
    }
  }
`;

export const UPDATE_SHARE_LINK = gql`
  mutation UpdateShareLink($_id: String!, $payload: UpdateShareLinkInput!) {
    updateShareLink(_id: $_id, payload: $payload)
  }
`;

export const SET_SHARE_LINK_PASSWORD = gql`
  mutation SetShareLinkPassword($_id: String!, $value: String!) {
    setShareLinkPassword(_id: $_id, value: $value)
  }
`;

export const REVEAL_SHARE_LINK_PASSWORD = gql`
  mutation RevealShareLinkPassword($_id: String!) {
    revealShareLinkPassword(_id: $_id) {
      password
      message
    }
  }
`;

export const ADD_SHARE_LINK_EMAILS = gql`
  mutation AddShareLinkEmails($_id: String!, $payload: ShareLinkEmailPayload!) {
    addShareLinkEmails(_id: $_id, payload: $payload) {
      modifiedCount
      emailWarning
    }
  }
`;

export const UPDATE_SHARE_LINK_PEOPLE_ROLE = gql`
  mutation UpdateShareLinkPeopleRole($_id: String!, $userId: String!, $role: String!) {
    updateShareLinkPeopleRole(_id: $_id, userId: $userId, role: $role)
  }
`;

export const DELETE_SHARE_LINK = gql`
  mutation DeleteShareLink($_id: String!) {
    deleteShareLink(_id: $_id)
  }
`;

// --- Public Queries & Mutations (share link access) ---

/**
 * Check share link access requirements before showing sign-in form.
 * Public endpoint — no auth context required.
 */
export const PRE_ACCESS_SHARE_LINK = gql`
  query PreAccessShareLink($objectId: String!, $token: String) {
    preAccessShareLink(objectId: $objectId, token: $token) {
      statusCode
      askPassword
      askEmail
      optionalEmail
      role
      teamId
      message
    }
  }
`;

/**
 * Validate share link credentials (password/email).
 * Public endpoint — no auth context required.
 */
export const DO_ACCESS_SHARE_LINK = gql`
  mutation DoAccessShareLink(
    $objectId: String!
    $payload: DoAccessShareLinkInput!
    $token: String
  ) {
    doAccessShareLink(objectId: $objectId, payload: $payload, token: $token) {
      statusCode
      message
      type
      accessType
      objectId
      role
      teamId
    }
  }
`;

/**
 * Grant Firebase custom claims after successful validation.
 * Public endpoint — requires Firebase token.
 */
export const POST_ACCESS_SHARE_LINK = gql`
  mutation PostAccessShareLink(
    $objectId: String!
    $payload: DoAccessShareLinkInput!
    $token: String!
  ) {
    postAccessShareLink(objectId: $objectId, payload: $payload, token: $token) {
      statusCode
      role
      teamId
    }
  }
`;

/**
 * Get file upload options for share link context (public, token-based).
 * Returns Transloadit params using owner's account config.
 */
export const GET_UPLOAD_OPTIONS_FOR_SHARE_LINK = gql`
  query GetUploadOptionsForShareLink($objectId: String!, $token: String!) {
    getUploadOptionsForShareLink(objectId: $objectId, token: $token)
  }
`;

/**
 * Save asset in share link context (public, token-based).
 */
export const SAVE_ASSET_FOR_SHARE_LINK = gql`
  mutation SaveAssetForShareLink(
    $objectId: String!
    $token: String!
    $payload: DoAccessShareLinkInput!
  ) {
    saveAssetForShareLink(objectId: $objectId, token: $token, payload: $payload)
  }
`;

/**
 * Generate SmallApp JWT token for share link context (public, token-based).
 * SmallApp REST backends verify this JWT instead of Firebase token.
 */
export const TOKEN_ASSET_SMALLAPP_FOR_SHARE_LINK = gql`
  query TokenAssetSmallAppForShareLink(
    $objectId: String!
    $token: String!
    $appType: String!
    $assetId: String
  ) {
    tokenAssetSmallAppForShareLink(
      objectId: $objectId
      token: $token
      appType: $appType
      assetId: $assetId
    )
  }
`;

/**
 * Get CDN settings for rendering assets in share link context.
 * Public endpoint — requires Firebase token.
 */
export const GET_APP_SETTINGS_FOR_SHARE_LINK = gql`
  query GetAppSettingsForShareLink($objectId: String!, $token: String!) {
    getAppSettingsForShareLink(objectId: $objectId, token: $token) {
      fileCdnkeyData
    }
  }
`;

/**
 * Get shared playlist or folder object.
 * Returns JSON object — shape depends on type (playlist fields vs folder fields).
 */
export const GET_SHARED_OBJECT = gql`
  query GetSharedObject($objectId: String!, $type: String!, $token: String!) {
    getSharedObject(objectId: $objectId, type: $type, token: $token)
  }
`;

/**
 * Get assets within a shared playlist or folder.
 * Returns array of asset JSON objects.
 */
export const GET_SHARED_ASSETS = gql`
  query GetSharedAssets($objectId: String!, $type: String!, $token: String!) {
    getSharedAssets(objectId: $objectId, type: $type, token: $token)
  }
`;

/**
 * Get company info for share link context (branding, features, plan).
 */
export const GET_SHARED_COMPANY = gql`
  query GetSharedCompany($objectId: String!, $token: String!) {
    getSharedCompany(objectId: $objectId, token: $token)
  }
`;
